import { createPhoneSchema } from '@/components/FormFieldPhone';
import { VendorDocument, VendorStatus } from '@/types/vendor.types';
import { ZIP_CODE_SCHEMA } from '@/utils/constant';
import { VendorResponseDocumentsItem } from '@brainvaulttech/dgr-react-sdk';
import { z } from 'zod';

const documentSchema = z
  .object({
    name: z.string().nullish(),
    mediaId: z.string().nullish(),
    document: z.instanceof(File).nullish(),
    mediaData: z.any().nullish(),
  })
  .refine(
    (data) => {
      if (data.document ?? data.mediaData) {
        return !!data.name && data.name.trim().length > 0;
      }
      return true;
    },
    {
      message: 'Document name is required',
      path: ['name'],
    }
  );

const commaSeparatedCoverageAreas = z
  .string({
    message: 'Coverage area is required',
  })
  .trim()
  .refine(
    (value) => {
      if (value === null || value === undefined || value.length == 0) {
        return true;
      }
      const pattern = /^[a-zA-Z0-9]+(,\s*[a-zA-Z0-9]+)*$/;
      if (!pattern.test(value)) {
        return false;
      }

      const coverageAreas = value.split(',').map((coverageArea) => coverageArea.trim());
      const coverageSchema = z.string({ message: 'Coverage area is invalid' }).max(10).min(3);
      for (const coverageArea of coverageAreas) {
        if (!coverageSchema.safeParse(coverageArea).success) {
          return false;
        }
      }
      return true;
    },
    {
      message: 'One or more coverage areas are invalid',
    }
  );

export const vendorDetailsSchema = z.object({
  address1: z.string({ message: 'Address line 1 is required' }),
  address2: z.string().nullable().optional(),
  city: z.string({ message: 'City is required' }),
  coverageAreas: commaSeparatedCoverageAreas,
  documents: z.array(documentSchema).optional(),
  email: z.string({ message: 'Email address is required' }).trim().email('Email address is invalid'),
  fees: z.string({ message: 'Fees is required' }),
  name: z.string({ message: 'Vendor name is required' }),
  notes: z.string().nullable().optional(),
  status: z.nativeEnum(VendorStatus).optional(),
  phone: createPhoneSchema(true),
  state: z.string({ message: 'State is required' }),
  taxId: z.string({ message: 'Tax ID is required' }),
  zipCode: ZIP_CODE_SCHEMA,
});
export interface DocumentType {
  id: string;
  name: string;
  document: File | null | undefined;
  mediaId?: string | null;
  mediaData?: VendorDocument | VendorResponseDocumentsItem | null;
}

export const vendorContactSchema = z
  .object({
    contactId: z.string().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    email: z.string().trim().email({ message: 'Email address is invalid' }).nullish(),
    phone: createPhoneSchema(false).optional().nullish(),
  })
  .superRefine((data, ctx) => {
    const hasAnyValue = !!data.firstName?.trim() || !!data.lastName?.trim() || !!data.email?.trim();

    // If all empty → valid
    if (!hasAnyValue) return;

    // Otherwise require all fields
    if (!data.firstName?.trim()) {
      ctx.addIssue({
        path: ['firstName'],
        message: 'First Name is required',
        code: 'custom',
      });
    }
    if (!data.lastName?.trim()) {
      ctx.addIssue({
        path: ['lastName'],
        message: 'Last Name is required',
        code: 'custom',
      });
    }
    if (!data.email?.trim()) {
      ctx.addIssue({
        path: ['email'],
        message: 'Email address is required',
        code: 'custom',
      });
    }
  });

export const vendorContactDetailsSchema = z.object({
  contacts: z.array(vendorContactSchema).optional().default([]),
});

export type vendorSchema = z.infer<typeof vendorDetailsSchema> & z.infer<typeof vendorContactDetailsSchema>;
